import { useCallback, useSyncExternalStore } from 'react';
import api, { apiRoutes } from '../api';
import { getConfig } from '../config/environment';
import {
  CONTENT_ADMIN_TOKEN_KEY,
  CONTENT_ADMIN_TOKEN_CHANGED_EVENT,
  clearAdminTokens,
  getAuthorizedContentAdminToken,
} from '../utils/adminToken';

export { CONTENT_ADMIN_TOKEN_KEY };

/**
 * Subscribe to localStorage changes for the content admin token.
 * Used by useSyncExternalStore to reactively update when token changes.
 */
const subscribe = (callback) => {
  const storageHandler = (e) => {
    if (e.key === CONTENT_ADMIN_TOKEN_KEY || e.key === null) {
      callback();
    }
  };
  window.addEventListener('storage', storageHandler);
  window.addEventListener(CONTENT_ADMIN_TOKEN_CHANGED_EVENT, callback);
  return () => {
    window.removeEventListener('storage', storageHandler);
    window.removeEventListener(CONTENT_ADMIN_TOKEN_CHANGED_EVENT, callback);
  };
};

/**
 * Get the current VALID content admin token from localStorage.
 * Expired or malformed tokens are cleared and reported as absent, so
 * consumers never see a stale "logged in" state.
 * @returns {string | null} The token or null if missing/expired.
 */
const getToken = () => getAuthorizedContentAdminToken(getConfig().tlfId);

/**
 * Hook for managing content admin authentication state.
 *
 * Provides reactive access to the content admin token stored in localStorage,
 * along with helper methods for logout and generating Authorization headers.
 *
 * @returns {Object} Content admin state and utilities
 * @returns {boolean} return.isContentAdmin - True if a content admin token exists
 * @returns {string | null} return.contentAdminToken - The current token or null
 * @returns {Function} return.logout - Logs out and optionally redirects
 * @returns {Function} return.getAuthHeader - Returns the Authorization header value
 *
 * @example
 * const { isContentAdmin, logout, getAuthHeader } = useContentAdmin();
 *
 * if (isContentAdmin) {
 *   const headers = { Authorization: getAuthHeader() };
 *   await api.post('/some-endpoint', data, { headers });
 * }
 *
 * // Logout and redirect to home
 * logout({ redirect: true });
 */
export function useContentAdmin() {
  // Reactively subscribe to token changes across tabs/windows
  const contentAdminToken = useSyncExternalStore(subscribe, getToken, () => null);
  const isContentAdmin = !!contentAdminToken;

  /**
   * Logs out the content admin by:
   * 1. Calling the backend logout endpoint (best-effort)
   * 2. Removing the token from localStorage
   * 3. Optionally redirecting or reloading the page
   *
   * @param {Object} [options] - Logout options
   * @param {boolean} [options.redirect=false] - If true, redirects to home page
   * @param {boolean} [options.reload=false] - If true, reloads the current page
   * @returns {Promise<void>}
   */
  const logout = useCallback(async ({ redirect = false, reload = false } = {}) => {
    // Best-effort backend logout
    try {
      await api.post(apiRoutes.AdminLogout);
    } catch {
      // Ignore errors - token removal is sufficient
    }

    clearAdminTokens();

    if (reload) {
      window.location.reload();
    } else if (redirect) {
      window.location.href = '/';
    }
  }, []);

  /**
   * Returns the Authorization header value for authenticated API requests.
   * Returns an empty string if not logged in as content admin.
   *
   * @returns {string} The Bearer token header value or empty string
   *
   * @example
   * const header = getAuthHeader();
   * if (header) {
   *   fetch('/api/endpoint', { headers: { Authorization: header } });
   * }
   */
  const getAuthHeader = useCallback(() => {
    const token = getAuthorizedContentAdminToken(getConfig().tlfId);
    return token ? `Bearer ${token}` : '';
  }, []);

  return {
    isContentAdmin,
    contentAdminToken,
    logout,
    getAuthHeader,
  };
}

export default useContentAdmin;
